import React, { useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Inbox,
  RefreshCw,
  TrendingUp,
  Users,
  Wallet,
  X,
} from "lucide-react";
import { Button } from "../ui/button";
import { AdminAttentionList } from "./AdminAttentionList";

const TONE = {
  green: "bg-emerald-50 text-emerald-800 border-emerald-100",
  amber: "bg-amber-50 text-amber-800 border-amber-100",
  red: "bg-red-50 text-red-700 border-red-100",
  blue: "bg-blue-50 text-blue-700 border-blue-100",
  gray: "bg-gray-100 text-gray-700 border-gray-200",
};

// Compact metric card — now a real <button> when `onClick` is provided so
// it gets the right keyboard semantics and screen-reader role. The current
// selection gets an emerald ring + soft glow so the admin can see what's
// driving the visible Needs Attention list.
const MetricCard = ({
  icon: Icon,
  label,
  value,
  tone = "gray",
  testid,
  active = false,
  onClick,
}) => {
  const base =
    "rounded-xl border bg-white p-4 flex items-start gap-3 text-left transition-all duration-150";
  const interactive = onClick
    ? "cursor-pointer hover:border-emerald-300 hover:shadow-md hover:-translate-y-[1px] focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2"
    : "";
  const selected = active
    ? "border-emerald-500 ring-2 ring-emerald-300 shadow-md bg-emerald-50/30"
    : "border-gray-200";
  const inner = (
    <>
      <span
        className={`w-9 h-9 rounded-lg flex items-center justify-center border ${TONE[tone]}`}
      >
        <Icon className="w-4 h-4" />
      </span>
      <div className="min-w-0">
        <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold">
          {label}
        </div>
        <div className="font-display text-2xl font-semibold text-gray-900 leading-tight mt-0.5">
          {value ?? "—"}
        </div>
      </div>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className={`${base} ${interactive} ${selected} w-full`}
        data-testid={testid}
        data-active={active ? "true" : "false"}
      >
        {inner}
      </button>
    );
  }
  return (
    <div className={`${base} ${selected}`} data-testid={testid}>
      {inner}
    </div>
  );
};

// Metric -> (predicate, label). Predicates run over `all_user_summaries`
// (full platform) so the visible filtered list always equals the card's
// number. Booleans (`is_paid`, `is_trial_active`, `is_active_7d`) are
// computed server-side using exactly the same logic that produces each
// card's count — no client-side drift.
const METRIC_DEFS = {
  total_users: { label: "all users", predicate: () => true },
  paid_users: {
    label: "paid users",
    predicate: (u) => !!u.is_paid,
  },
  trial_users: {
    label: "trial users",
    predicate: (u) => !!u.is_trial_active,
  },
  active_7d: {
    label: "users active in the last 7 days",
    predicate: (u) => !!u.is_active_7d,
  },
  zero_leads: {
    label: "users with zero leads",
    predicate: (u) => (u.total_leads ?? 0) === 0,
  },
  trials_ending_7d: {
    label: "users with a trial ending in 7 days",
    predicate: (u) => (u.reason_flags || []).includes("trial_ending_soon"),
  },
  total_leads: {
    label: "users with at least one lead",
    predicate: (u) => (u.total_leads ?? 0) > 0,
  },
  booked_leads: {
    label: "users with booked leads",
    predicate: (u) => (u.booked_leads ?? 0) > 0,
  },
  reviews_sent: {
    label: "users who sent review requests",
    predicate: (u) => (u.review_requests_sent ?? 0) > 0,
  },
  rebooks_sent: {
    label: "users who sent rebook reminders",
    predicate: (u) => (u.rebook_reminders_sent ?? 0) > 0,
  },
};

export const AdminHealth = ({ data, loading, error, onRefresh, onAttentionPatched }) => {
  const um = data?.user_metrics || {};
  const am = data?.activity_metrics || {};
  const lm = data?.lead_metrics || {};
  const usem = data?.usage_metrics || {};
  const tm = data?.trial_risk_metrics || {};

  // null = no filter. A non-null string is the metric key from METRIC_DEFS
  // and drives both the highlighted card and the filtered Needs Attention list.
  const [selectedMetric, setSelectedMetric] = useState(null);

  const cards = useMemo(
    () => [
      { key: "total_users", icon: Users, label: "Total users", value: um.total_users, tone: "blue", testid: "health-card-total-users" },
      { key: "paid_users", icon: Wallet, label: "Paid users", value: um.paid_users, tone: "green", testid: "health-card-paid-users" },
      { key: "trial_users", icon: Users, label: "Trial users", value: um.trial_users, tone: "amber", testid: "health-card-trial-users" },
      { key: "active_7d", icon: Activity, label: "Active last 7d", value: am.active_users_last_7_days, tone: "green", testid: "health-card-active-7d" },
      { key: "zero_leads", icon: Inbox, label: "Zero leads", value: am.users_with_zero_leads, tone: "gray", testid: "health-card-zero-leads" },
      { key: "trials_ending_7d", icon: AlertTriangle, label: "Trial ending ≤ 7d", value: tm.trials_ending_next_7_days, tone: "amber", testid: "health-card-trials-ending-7d" },
      // User-count cards: the click filters by users-who-have-x, so the
      // card's value mirrors that count (not the total event count) to keep
      // the "card number == visible list count" promise.
      { key: "total_leads", icon: Inbox, label: "Users with leads", value: am.users_with_at_least_one_lead, tone: "blue", testid: "health-card-total-leads" },
      { key: "booked_leads", icon: CheckCircle2, label: "Users with bookings", value: lm.users_with_booked_leads, tone: "green", testid: "health-card-booked-leads" },
      { key: "reviews_sent", icon: TrendingUp, label: "Sent review requests", value: usem.users_with_reviews_sent, tone: "gray", testid: "health-card-reviews" },
      { key: "rebooks_sent", icon: TrendingUp, label: "Sent rebook reminders", value: usem.users_with_rebook_reminders_sent, tone: "gray", testid: "health-card-rebooks" },
    ],
    [um, am, lm, usem, tm],
  );

  const onCardClick = (key) => {
    // Second click clears the filter — natural toggle.
    setSelectedMetric((prev) => (prev === key ? null : key));
  };

  const activeDef = selectedMetric ? METRIC_DEFS[selectedMetric] : null;

  // When a metric card is selected: filter the FULL user population so the
  // visible list always equals the card's number. With no card selected we
  // fall back to the curated top-20 `attention_users` list — the normal
  // Needs Attention experience.
  const allSummaries = data?.all_user_summaries || [];
  const attentionUsers = data?.attention_users || [];
  const filteredAll = useMemo(
    () => (activeDef ? allSummaries.filter(activeDef.predicate) : null),
    [activeDef, allSummaries],
  );
  const listUsers = activeDef ? filteredAll : attentionUsers;
  const activeCount = filteredAll ? filteredAll.length : null;

  return (
    <section
      className="mt-10 rounded-2xl bg-white border border-gray-200 overflow-hidden"
      data-testid="admin-health-section"
    >
      <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
            <Activity className="w-4 h-4 text-emerald-700" />
          </span>
          <div>
            <h2 className="font-display text-lg font-semibold text-gray-900">
              Customer Health
            </h2>
            <p className="text-xs text-gray-500">
              Platform usage, trial pipeline, and accounts that need attention.
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={onRefresh}
          disabled={loading}
          data-testid="admin-health-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh health
        </Button>
      </header>

      {error ? (
        <div className="px-5 py-10 text-center" data-testid="admin-health-error">
          <p className="text-red-600 font-medium">{error}</p>
          <Button
            type="button"
            variant="outline"
            className="mt-3"
            onClick={onRefresh}
            data-testid="admin-health-retry"
          >
            Try again
          </Button>
        </div>
      ) : loading && !data ? (
        <div className="p-5" data-testid="admin-health-loading">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {Array.from({ length: 10 }).map((_, i) => (
              <div
                key={i}
                className="h-20 rounded-xl border border-gray-200 bg-gray-50 animate-pulse"
              />
            ))}
          </div>
          <div className="mt-6 h-5 w-32 bg-gray-100 rounded animate-pulse" />
          <div className="mt-3 space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-12 rounded-lg border border-gray-100 bg-gray-50 animate-pulse"
              />
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {cards.map((c) => (
              <MetricCard
                key={c.testid}
                {...c}
                active={selectedMetric === c.key}
                onClick={() => onCardClick(c.key)}
              />
            ))}
          </div>

          {/* Active filter banner — appears only when a metric card is selected.
              Tells the admin exactly what they're looking at, includes the
              filtered count for instant card-vs-list parity, and offers a clear
              one-click escape hatch. */}
          {activeDef ? (
            <div
              className="mx-5 mb-3 flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2"
              data-testid="admin-health-active-filter"
            >
              <p className="text-xs sm:text-sm text-emerald-900">
                <span className="font-semibold">Showing:</span>{" "}
                <span data-testid="admin-health-active-filter-label">
                  {activeDef.label} ({activeCount})
                </span>
              </p>
              <button
                type="button"
                onClick={() => setSelectedMetric(null)}
                className="text-xs font-semibold text-emerald-800 hover:text-emerald-900 flex items-center gap-1"
                data-testid="admin-health-clear-filter"
              >
                <X className="w-3.5 h-3.5" /> Clear filter
              </button>
            </div>
          ) : null}

          <AdminAttentionList
            users={listUsers}
            onPatched={onAttentionPatched}
            externalActive={!!activeDef}
            externalLabel={activeDef ? activeDef.label : null}
          />
        </>
      )}
    </section>
  );
};
